/**
 * Geometry for the Daily Brief dock flight ("the panel docks into / out of the
 * header bell").
 *
 * On CLOSE the panel card is mounted, so its resting rect is measured live. On
 * OPEN the card hasn't mounted yet (its entrance is the flight), so we replicate
 * the CSS resting geometry from the always-mounted CONTAINER instead — one
 * computation per transition, no per-frame DOM measuring.
 */

import type { DockRect } from '@/common/motion/chatDockMotion';

// ── Solo placement geometry ──────────────────────────────────────────────────
// In solo chat the Brief docks as the TOP THIRD of .conversation-chat-panel
// (a 2-row grid, rows 1fr/2fr when open), centered in the chat's 680px lane:
//   .conversation-chat-panel > .daily-summary-panel-card.daily-summary-modal
//     { max-width: 680px; margin: 16px auto 8px }   (DailySummaryModal.css)
// KEEP IN SYNC with those rules + --chat-lane-max-width (ChatBox.css).
const SOLO_CARD_LANE_MAX_PX = 680;
const SOLO_CARD_MARGIN_TOP = 16;
const SOLO_CARD_MARGIN_BOTTOM = 8;

/**
 * Resting rect of the solo Daily Brief card — the top-third row of the chat
 * panel container, centered in the chat lane. Used as the OPEN flight's
 * landing target before the card mounts.
 */
export function computeBriefPanelRect(containerRect: DockRect): DockRect {
  const rowHeight = containerRect.height / 3;
  const width = Math.max(0, Math.min(SOLO_CARD_LANE_MAX_PX, containerRect.width));
  return {
    top: containerRect.top + SOLO_CARD_MARGIN_TOP,
    left: containerRect.left + (containerRect.width - width) / 2,
    width,
    height: Math.max(0, rowHeight - SOLO_CARD_MARGIN_TOP - SOLO_CARD_MARGIN_BOTTOM),
  };
}

// ── Module-open SPLIT placement geometry ─────────────────────────────────────
// In the split view the Brief docks as the TOP THIRD of the chat-box grid's
// RIGHT column. Mirrors --chat-split-right-col: clamp(360px, 33vw, 540px) and the
// `grid-template-rows: 1fr 2fr` + .chat-brief-pane margins in ChatBox.css.
// KEEP IN SYNC with those rules.
const SPLIT_COL_MIN_PX = 360;
const SPLIT_COL_MAX_PX = 540;
const SPLIT_COL_VW_FRACTION = 0.33;
// .chat-brief-pane { margin: 16px 16px 8px 8px } (top right bottom left).
const SPLIT_PANE_MARGIN_TOP = 16;
const SPLIT_PANE_MARGIN_RIGHT = 16;
const SPLIT_PANE_MARGIN_BOTTOM = 8;
const SPLIT_PANE_MARGIN_LEFT = 8;

/**
 * Resting rect of the docked Brief in the module-open split — the top row (1/3
 * height) of the chat-box-root's right column, inset by the .chat-brief-pane
 * margins. Used as the OPEN flight's landing target before the split pane mounts.
 */
export function computeBriefSplitRect(rootRect: DockRect, viewportWidth: number): DockRect {
  const colWidth = Math.min(
    Math.max(SPLIT_COL_MIN_PX, viewportWidth * SPLIT_COL_VW_FRACTION),
    SPLIT_COL_MAX_PX,
    rootRect.width,
  );
  // Top row of `1fr 2fr` is one third of the grid height.
  const rowHeight = rootRect.height / 3;
  return {
    top: rootRect.top + SPLIT_PANE_MARGIN_TOP,
    left: rootRect.left + rootRect.width - colWidth + SPLIT_PANE_MARGIN_LEFT,
    width: Math.max(0, colWidth - SPLIT_PANE_MARGIN_LEFT - SPLIT_PANE_MARGIN_RIGHT),
    height: Math.max(0, rowHeight - SPLIT_PANE_MARGIN_TOP - SPLIT_PANE_MARGIN_BOTTOM),
  };
}
